/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  AfterContentChecked,
  Component,
  ContentChild,
  ElementRef,
  forwardRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TranslocoService, TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { Popover } from 'primeng/popover';
import {
  ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { MenuItem, MenuItemClickEvent } from '../types';
import { requiresDiscernableText } from './menu-list-panel-util';
import { BulbMenusTranslationProvider } from '../translation-provider';
import { findSelectedOption } from '../radio-list/radio-list.utilities';

export enum MenuListType {
  MENU = 'MENU',
  RADIO = 'RADIO',
}

@Component({
  selector: 'bulb-menu-list-panel',
  templateUrl: './menu-list-panel.component.html',
  styleUrls: ['./menu-list-panel.component.scss'],
  providers: [
    BulbMenusTranslationProvider,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MenuListPanelComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class MenuListPanelComponent implements OnInit, OnDestroy, AfterContentChecked, ControlValueAccessor {
  // The button template passed in from the parent component.
  @ContentChild('buttonTemplate') buttonTemplate: TemplateRef<any>;

  // The button trigger element in @ContentChild('buttonTemplate').
  @ContentChild('toggleEl', { read: ElementRef }) toggleElRef: ElementRef<any>;

  // The footer template passed in from the parent component.
  @ContentChild('footerTemplate') footerTemplate: TemplateRef<any>;

  // The default button template, found in this component.
  @ViewChild('buttonTemplate') buttonTemplateDefault: TemplateRef<any>;

  // The default button trigger element, found in @ViewChild('buttonTemplate').
  @ViewChild('toggleEl', { read: ElementRef }) toggleElRefDefault: ElementRef<any>;

  @Input() menuItems: Array<MenuItem>;

  /*
  * When using the menu within the fixed position container,
  * we need to apply some styles to the menu to keep it in a place.
  */
  @Input() useFixedPosition: boolean = false;

  @Input() buttonLabelKey: string = 'menuListPanel.moreOptions';

  @Input() listType: MenuListType = MenuListType.MENU;

  @Input() positionCenter: boolean = false;

  @ViewChild('popover') popover: Popover;

  onChange = (value: any) => {};

  onTouched = () => {};

  public isOpen: boolean = false;

  private value: any;

  public formGroup: UntypedFormGroup = this.fb.group({});

  private translationSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private transloco: TranslocoService,
    @Inject(TRANSLOCO_SCOPE) private scope,
  ) { }

  writeValue(value: any): void {
    this.value = value;
    this.onChange(value);
    this.formGroup.patchValue({
      radioListControl: value,
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    this.formGroup.addControl('radioListControl', new UntypedFormControl(findSelectedOption(this.menuItems, this.value)));

    // TODO: unsubscribe
    this.formGroup.valueChanges.subscribe((value) => {
      this.onChange(value.radioListControl);
    });
  }

  /**
   * When a custom template is supplied, we need to check the template for discernable text.
   * If there is no discernable text, we add an aria-label to the button.
   */
  ngAfterContentChecked(): void {
    if (!this.buttonTemplate || !this.toggleElRef) return;
    if (requiresDiscernableText(this.toggleElRef.nativeElement) && !this.translationSubscription) {
      this.translationSubscription = this.transloco.selectTranslate(this.buttonLabelKey, {}, this.scope)
        .subscribe((label: string) => {
          this.toggleElRef.nativeElement.setAttribute('aria-label', label);
          this.translationSubscription?.unsubscribe();
          this.translationSubscription = undefined;
        });
    }
  }

  /**
   * The toggle button is dynamically created by the parent component,
   * so we need to listen for clicks on the Host (this component) to toggle the panel.
   * Click events cannot be attached to an ng-container, so we listen on the Host,
   * then toggle the popover if the click event is on the toggle element ref.
   */
  @HostListener('click', ['$event'])
  public toggle(event: Event): void {
    if (this.toggleElRef && !this.toggleElRef.nativeElement.contains(event.target)) return;
    if (this.toggleElRefDefault && !this.toggleElRefDefault.nativeElement.contains(event.target)) return;
    this.popover.toggle(event);
    this.isOpen = !this.isOpen;
  }

  /**
   * When a menu item is clicked, this function will be called and
   * check the value of closeMenuOnClick and act accordingly.
   * @param event event of type MenuItemClickEvent.
   */
  public menuItemClicked(event: MenuItemClickEvent): void {
    if (event.menuItem.closeMenuOnClick) {
      this.popover.hide();
      this.isOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.translationSubscription?.unsubscribe();
  }
}
